import { v4 as uuidv4 } from 'uuid';
import { auth } from '@/api';
import i18n from '@/utils/plugins/i18n';
import { country } from '@/utils/plugins/config';
import {
	isKaspiDisabled,
	isOnlyKaspiAvailable
} from '@/utils/constants/kaspi-availability';
import { isApplePayNativeDisabled } from '@/utils/constants/web-payment';
import { AuthService } from '../auth';
import {
	IOneClickPaymentOptions,
	IPaymentMethod,
	IKaspiLinkPayload,
	IUserEvent
} from './types';
import { analytics } from '@/utils/analytics-factory';
import router from '@/router';
import { transliterate } from '@/utils/functions';
import { application } from '@/utils/plugins/di/providers';

type PaymentWidget = {
	init: () => void;
	destroy: () => void;
	updateLocale: (locale: string) => void;
	setDisabled: (disabled: boolean) => void;
	setLoading: (loading: boolean) => void;
	updateAmount: (amount: number) => void;
	logout: () => void;
	setMerchantOrderId: (orderId: string) => void;
	setPreorderId: (preorderId: number) => void;
	setIdempotencyKey: (key: string) => void;
	updateTrackId: (trackId: string) => Promise<boolean>;
	pay: () => void;
	updateAvailableBonus: (availableBonus: number) => void;
	resumeOrderStatusCheck: () => void;
};

let paymentWidget: PaymentWidget | null = null;

const init = async (options: IOneClickPaymentOptions) => {
	let trackId = '';
	let bonuses;

	if (AuthService.isAuthorized()) {
		await auth.getTrackId().then(response => {
			trackId = response.data.id;
		});
	}

	if (options.bonuses?.amount) {
		bonuses = options.bonuses;
	}

	const { default: ChocoOneClickWidget } = await import(
		'choco-one-click-payment'
	);

	const isReactNativeWebview =
		typeof window.ReactNativeWebView?.postMessage === 'function';

	paymentWidget = new ChocoOneClickWidget({
		trackId,
		clientId: import.meta.env.VITE_PAYMENT_CLIENT_ID,
		projectId: window.ChocoClientId,
		terminalId: options.terminalId,
		isFridge: options.isFridge,
		amount: options.amount,
		idempotencyKey: options.idempotencyKey,
		currency: options.currency,
		country: country.value?.code,
		environment:
			import.meta.env.VITE_ENV === 'production' ? 'production' : 'stage',
		locale: i18n.global.locale.value,
		os: application.getClientOs(),
		color: window.ReactNativeWebView?.color || '#ff511b',
		bonuses,
		disableKaspi: isKaspiDisabled(options.terminalId, options.isFridge),
		onlyKaspiAvailable: isOnlyKaspiAvailable(options.terminalId),
		// Именно ANDROID, а не «мы в вебвью»: window.RahmetApp есть и в iOS-сборке
		// супераппа, где нативный путь Google Pay неприменим.
		isNativeAndroid:
			application.getClientOs() === 'ANDROID' || isReactNativeWebview,
		disableApplePayNative: isApplePayNativeDisabled(),
		...(isReactNativeWebview &&
			window.ReactNativeWebView?.brand && {
				merchantName: transliterate(window.ReactNativeWebView.brand)
			}),
		disableUseBalance: options.disable_chocobalance_button,
		onPayWithGooglePay: ({ amount, gateway, gatewayMerchantId }) => {
			return new Promise<string>((resolve, reject) => {
				if (isReactNativeWebview) {
					window.ReactNativeWeb!.onGooglePaySuccess = resolve;
					window.ReactNativeWeb!.onGooglePayError = message =>
						reject(new Error(message));
					window.ReactNativeWeb!.onGooglePayCancelled = () =>
						reject({ statusCode: 'CANCELED' });
					window.ReactNativeWeb!.payWithGooglePay!({
						amount,
						gateway,
						gatewayMerchantId
					});
				} else {
					window.RahmetWebApp.onGooglePaySuccess = resolve;
					window.RahmetWebApp.onGooglePayError = message =>
						reject(new Error(message));
					window.RahmetWebApp.onGooglePayCancelled = () =>
						reject({ statusCode: 'CANCELED' });
					window.RahmetApp.payWithGooglePay!(
						JSON.stringify({ amount, gateway, gatewayMerchantId })
					);
				}
			});
		},
		onSuccess: data => {
			options.onSuccess?.(data);
		},
		onError: error => {
			options.onError?.(error as { attributes: { message: string } });
		},
		onCancel: () => {
			options.onCancel?.();
		},
		onConfirmPay: async (method: unknown) => {
			options.onConfirmPay?.(method as IPaymentMethod);
		},
		onAppliedBonus: (value: boolean) => {
			options.onAppliedBonus?.(value);
		},
		onCouponSuppressingCashback: (active: boolean) => {
			options.onCouponSuppressingCashback?.(active);
		},
		onPaymentMethodSelected: (method: unknown) => {
			options.onPaymentMethodSelected?.(method as IPaymentMethod);
		},
		onKaspiLink: (payload: IKaspiLinkPayload) => {
			options.onKaspiLink?.(payload);
		},
		onUserEvent: (event: IUserEvent) => {
			analytics.logEvent(event.eventName, event.eventProperties);
		},
		onFreedomLink: () => {
			options.onFreedomLink?.();
		},
		onBeforeCheckout: options.onBeforeCheckout,
		onRequestNewTrackId: async () => {
			const response = await auth.getTrackId();
			return response.data.id;
		}
	});

	const currentRouteName = router.currentRoute.value.name;
	if (currentRouteName !== 'Bill' && currentRouteName !== 'Cart') {
		return;
	}

	paymentWidget.init();
};

const destroy = () => {
	if (paymentWidget) {
		paymentWidget.destroy();
		paymentWidget = null;
	}
};

const locale = (locale: string) => {
	if (paymentWidget) {
		paymentWidget.updateLocale(locale);
	}
};

const disable = (disabled: boolean) => {
	if (paymentWidget) {
		paymentWidget.setDisabled(disabled);
	}
};

const loading = (loading: boolean) => {
	if (paymentWidget) {
		paymentWidget.setLoading(loading);
	}
};

const amount = (amount: number) => {
	if (paymentWidget) {
		paymentWidget.updateAmount(amount);
	}
};

// Виджет, созданный без trackId (юзер был неавторизован), остаётся в состоянии
// «Войти»: выбор метода заблокирован, метод оплаты не выбран. Сам он из этого
// состояния не выйдет — после авторизации хост обязан отдать ему свежий trackId,
// иначе следующий pay() упрётся в «Метод оплаты не выбран».
const updateTrackId = async (): Promise<boolean> => {
	if (!paymentWidget) {
		return false;
	}

	try {
		const response = await auth.getTrackId();
		return await paymentWidget.updateTrackId(response.data.id);
	} catch {
		return false;
	}
};

const logout = async () => {
	const { default: ChocoOneClickWidget } = await import(
		'choco-one-click-payment'
	);
	ChocoOneClickWidget.logout();
};

const pay = (orderId: number, preorderId: number) => {
	if (paymentWidget) {
		paymentWidget.setMerchantOrderId(String(orderId));
		paymentWidget.setPreorderId(preorderId);
		paymentWidget.pay();
	}
};

const updateAvailableBonus = (availableBonus: number) => {
	if (paymentWidget) {
		paymentWidget.updateAvailableBonus(availableBonus);
	}
};

const refreshIdempotencyKey = () => {
	if (paymentWidget) {
		paymentWidget.setIdempotencyKey(uuidv4());
	}
};

const triggerPay = () => {
	if (paymentWidget) {
		paymentWidget.pay();
	}
};

// Fallback для webview супераппа: при возврате из приложения банка (Kaspi Pay)
// webview может не эмитить visibilitychange/focus/pageshow. Нативный слой дёргает
// это через becomingActive(), чтобы виджет дотянул статус незакрытого заказа.
const resumeOrderStatus = () => {
	if (paymentWidget) {
		paymentWidget.resumeOrderStatusCheck();
	}
};

const WebPaymentService = {
	init,
	destroy,
	locale,
	disable,
	loading,
	amount,
	pay,
	triggerPay,
	updateTrackId,
	logout,
	updateAvailableBonus,
	refreshIdempotencyKey,
	resumeOrderStatus
};

export { WebPaymentService };
